import * as React from "react";
import { motion } from "framer-motion";
import * as Accordion from "@radix-ui/react-accordion";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  icon?: string;
  iconPosition?: "left" | "right";
}

interface FaqAccordionProps {
  data: FAQItem[];
  className?: string;
  timestamp?: string;
  questionClassName?: string;
  answerClassName?: string;
}

export function FaqAccordion({
  data,
  className,
  timestamp = "Every day, 9:01 AM",
  questionClassName,
  answerClassName,
}: FaqAccordionProps) {
  const [openItem, setOpenItem] = React.useState<string | null>(null);

  return (
    <div className={cn("w-full mx-auto", className)}>
      {timestamp && (
        <p className="text-xs text-muted-foreground text-center mb-6">
          {timestamp}
        </p>
      )}

      <Accordion.Root
        type="single"
        collapsible
        value={openItem ?? undefined}
        onValueChange={(value) => setOpenItem(value)}
      >
        {data.map((item) => (
          <Accordion.Item
            key={item.id}
            value={item.id.toString()}
            className="mb-2"
          >
            <Accordion.Header>
              <Accordion.Trigger className="flex items-center gap-1.5 group text-left w-full">
                {/* Icon on left */}
                {item.icon && item.iconPosition === "left" && (
                  <span className="text-base flex-shrink-0 -mt-0.5">{item.icon}</span>
                )}

                {/* Question pill */}
                <span
                  className={cn(
                    "inline-flex items-center rounded-full px-4 py-2.5 text-sm font-medium transition-colors",
                    "bg-muted hover:bg-muted/80",
                    questionClassName
                  )}
                >
                  {item.question}
                </span>

                {/* Icon on right */}
                {item.icon && item.iconPosition === "right" && (
                  <span className="text-base flex-shrink-0 -mt-0.5">{item.icon}</span>
                )}

                {/* Plus/Minus */}
                <span className="flex-shrink-0 ml-1">
                  {openItem === item.id.toString() ? (
                    <Minus className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  )}
                </span>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="pt-2 pb-1 pl-4"
              >
                <div className="flex justify-end">
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-3 text-sm max-w-[85%]",
                      "bg-foreground text-background",
                      answerClassName
                    )}
                  >
                    {item.answer}
                  </div>
                </div>
              </motion.div>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </div>
  );
}
